import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { maxBornOnDate, minBornOnDate } from '@app/constants/dates';
import { EmploymentTypes } from '@app/constants/employment-types';
import { TerminationCode, TerminationCodes } from '@app/constants/termination-codes';
import { AnalyticEvents } from '@app/enums';
import { isSameModel } from '@app/functions';
import { resetCursorPosition } from '@app/functions/reset-cursor-position';
import { stripNonNumeric } from '@app/functions/strip-non-numeric';
import { Account } from '@app/models/account/account.model';
import { RoleAssignment } from '@app/models/account/role-assignment.model';
import { Role } from '@app/models/account/role.model';
import { Company } from '@app/models/company/company.model';
import { Job } from '@app/models/company/job.model';
import { Office } from '@app/models/company/office.model';
import { Address } from '@app/models/employee/address.model';
import { Employee } from '@app/models/employee/employee.model';
import { Salary } from '@app/models/employee/salary.model';
import { TerminationService } from '@app/modules/employees/services';
import { OnboardingStatusService } from '@app/modules/self-serve/services/onboarding-status.service';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { EmploymentTypeDisplayOptions } from '@app/types/translatables/employment-type-display.options';
import { createNumberMask } from 'text-mask-addons';

/**
 * EmployeeProfileView allows for creating regular employees and terminated employees
 * The view is different depending on whether the employee is terminated or not.
 * It's possible these should be separate views, but they feel similar enough that I'm leaving them together.
 */
@Component({
    templateUrl: './employee-profile.template.html',
    styleUrls: ['../../../self-serve.style.scss'],
})
export class EmployeeProfileView implements OnInit {
    @ViewChild('form', { static: true }) form!: NgForm;
    isLoading = true;
    isSaving = false;
    sendInvite = true;
    /**
     * isAddingTerminatedEmployee is used to modify the template
     * to show UI only applicable to terminated employees
     */
    isAddingTerminatedEmployee = false;
    company: Company = new Company();
    employee: Employee = new Employee();
    account: Account = new Account();
    employeeAddress: Address = new Address();
    offices: Office[] = [];
    jobs: Job[] = [];
    newJob = new Job();
    salary: Salary = new Salary({ frequency: 'year', name: 'Regular Pay', isPrimary: true });
    currencyMask = createNumberMask({
        prefix: '$',
        includeThousandsSeparator: true,
        allowDecimal: true,
        guide: true,
        keepCharPositions: false,
    });
    terminationCodes = TerminationCodes;
    isSameModel = isSameModel;
    showCreateJobModal = false;

    readonly minBornOnDate = minBornOnDate;
    readonly maxBornOnDate = maxBornOnDate;
    readonly employmentTypeValues: string[] = EmploymentTypes;
    readonly employmentTypeDisplay = EmploymentTypeDisplayOptions;

    private employeeRole: Role = new Role();

    constructor(
        private analyticService: AnalyticService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private onboardingStatusService: OnboardingStatusService,
        private notifyService: NotifyService,
        private terminationService: TerminationService
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            this.isAddingTerminatedEmployee = this.router.url.includes('new-terminated');
            if (this.isAddingTerminatedEmployee) {
                this.sendInvite = false;
            }

            const [company, [offices], [roles], [jobs]] = await Promise.all([
                Company.find(this.authService.company.id),
                Office.param('company', this.authService.company.id).all(),
                Role.where('company_id', this.authService.company.id)
                    .where('assignableBy', this.authService.role.id)
                    .all(),
                Job.param('company', this.authService.company.id).orderBy('title', 'ASC').all(),
            ]);

            const employeeRole = roles.find((role) => role.name === 'Employee' && role.isImmutable);
            if (!employeeRole) {
                // this should never happen
                // if it does, fail loudly because we can't recover from not having roles
                this.notifyService.error('Missing role');
                throw new Error('missing role in self serve add employee');
            }

            this.company = company;
            this.offices = offices;
            this.employeeRole = employeeRole;
            this.jobs = jobs;

            // set default office if there is only one
            if (!this.employee.officeId && offices.length === 1) {
                this.employee.officeId = offices[0].id;
            }
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.isLoading = false;
        }
    }

    async onSubmit(): Promise<void> {
        if (!this.form.valid || this.isSaving) {
            return;
        }

        try {
            this.isSaving = true;

            /**
             * We are going to create the resources for this new user/employee.
             * The order matters.
             */

            // create account
            this.account.legalFirstName = this.employee.firstName ?? '';
            this.account.legalLastName = this.employee.lastName ?? '';
            if (this.isAddingTerminatedEmployee) {
                this.account.isLocked = true;
                this.account.isActive = false;
                this.analyticService.trackEvent(AnalyticEvents.SelfServeAddTeamClickSaveTerminated);
            } else {
                this.analyticService.trackEvent(AnalyticEvents.SelfServeAddTeamClickSaveEmployee);
            }
            await this.account.save();

            // create employee
            this.employee.accountId = this.account.id;
            this.employee.status = 'onboarding';
            this.employee.currency = 'CAD';
            if (this.isAddingTerminatedEmployee) {
                this.employee.isPayrollSyncEnabled = false;
                this.employee.collectHiringQuestions = false;
                this.employee.wasAddedByTerminationFlow == true;
            }
            await this.employee.param('company', this.authService.company.id).save();

            // create salary
            // we allow no frequency and thus no salary
            if (this.salary.frequency) {
                this.salary.employeeId = this.employee.id;
                this.salary.effectiveAt = this.employee.hiredAt ? new Date(this.employee.hiredAt) : new Date();
                await this.salary.param('company', this.company.id).param('employee', this.employee.id).save();
            }

            // assign role
            await new RoleAssignment({ roleId: this.employeeRole.id }).param('account', this.account.id).save();

            // create address
            if (this.isAddingTerminatedEmployee) {
                await this.employeeAddress
                    .param('company', this.authService.company.id)
                    .param('employee', this.employee.id)
                    .save();
            }

            // send invite
            if (this.sendInvite) {
                await this.authService.sendInvitations([this.account.id], this.authService.company.id);
            }

            // terminate
            if (this.isAddingTerminatedEmployee) {
                // these values will be populated, but I'm making typescript happy.
                this.terminationService.terminate(
                    this.employee,
                    new Date(this.employee.lastDayOfWorkOn || ''),
                    this.employee.terminationReasonCode as TerminationCode,
                    this.employee.terminationComments || ''
                );
            }

            this.onboardingStatusService.refresh();
            this.router.navigate(['..'], { relativeTo: this.route });
        } catch (error) {
            this.notifyService.error(error);
            this.isSaving = false;
        }
    }

    clearPosition(): void {
        this.newJob = new Job();
        this.showCreateJobModal = false;
    }

    async onCreatePosition(job: Job): Promise<void> {
        try {
            await job.param('company', this.authService.company.id).save();
            this.jobs.push(job);
            this.employee.job = job;
            this.jobs.sort((a: Job, b: Job) => a.title.localeCompare(b.title));
        } catch (error) {
            this.notifyService.error(error);
        } finally {
            this.clearPosition();
        }
    }

    stripNonNumeric(val: string | number): number {
        if (!val) {
            return 0;
        }

        return stripNonNumeric(val) ?? 0;
    }

    resetCursorPosition(): void {
        resetCursorPosition();
    }

    trackSelfServeOnboardingInviteCheckbox(): void {
        if (!this.sendInvite) {
            this.analyticService.trackEvent(AnalyticEvents.SelfServeAddTeamClickInviteCheckbox);
        }
    }
}
